import type { FC, RefObject } from 'react';
import React, { useState, useEffect } from 'react';
import type { WrappedComponentProps } from 'react-intl-next';
import { defineMessages, injectIntl, FormattedMessage } from 'react-intl-next';
import { styled } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import TextArea from '@atlaskit/textarea';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Checkbox } from '@atlaskit/checkbox';
import Form, { Field, HelperMessage } from '@atlaskit/form';
import { token } from '@atlaskit/tokens';

import { fireScreenEvent } from './AnalyticsEvents.tsx';
import { ARCHIVE_PAGES_WITH_NOTE_CHARACTER_LIMIT } from './archivingConstants.ts';

import { fg } from '@confluence/feature-gating';

export const i18n = defineMessages({
	notePlaceholder: {
		id: 'archive-pages.archive.dialog.note.placeholder',
		defaultMessage: 'Let others know why this was archived.',
		description:
			'placeholder for a input that allows you to add a descriptive message for why you are archiving the page.',
	},
	noteTitle: {
		id: 'archive-pages.archive.dialog.note.title',
		defaultMessage: 'Note (optional)',
		description:
			'title above an input that allows you to add a descriptive message for why you are archiving the page.',
	},
	applyNoteToNestedPagesLabel: {
		id: 'archive-pages.archive.dialog.note.apply-note-to-nested-message',
		defaultMessage: 'Add note to nested items, too',
		description:
			'The label for the checkbox that indicates if the user wants to save the note to the nested pages in addition to the parent page.',
	},
	characterLimitError: {
		id: 'archive-pages.archive.dialog.note.character-limit-error-message',
		defaultMessage: 'You exceeded the character limit.',
		description:
			'The error message that indicates the user has typed more characters than are allowed to be saved in an archive note.',
	},
});

const nestedCheckboxWrapperStyles = xcss({
	marginTop: 'space.100',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InverseLabelColor = styled.span<{ isColorInverted?: boolean }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props?.isColorInverted ? token('color.text.inverse') : undefined),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorWrapper = styled.div<{ isColorInverted?: boolean }>({
	display: 'flex',
	// During dark-mode tokenization in CCECO-700 we were unable to identify an appropriate
	// design-system token to use to represent this color in such a way that had sufficient
	// contrast with the background colors available in both light and dark modes. As a
	// result we're leaving this as custom for now but adding it as a request to our design
	// team to identify the best path forward for this color.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: (props) => (props?.isColorInverted ? '#FFBDAD' : token('color.text.danger')),
});

type ArchivedPageNoteInputProps = {
	noteRef: RefObject<HTMLTextAreaElement>;
	includeNestedCheckbox?: boolean;
	onNestedCheckboxChange?: () => void;
	isNestedCheckboxChecked?: boolean;
	analyticsSource: string;
	defaultValue?: string;
	onValidate: (isValid: boolean) => void;
	isColorInverted?: boolean;
	isArchiveInProgress?: boolean;
};

const ArchivedPageNoteInputComponent: FC<ArchivedPageNoteInputProps & WrappedComponentProps> = ({
	noteRef,
	includeNestedCheckbox,
	onNestedCheckboxChange,
	isNestedCheckboxChecked,
	analyticsSource,
	defaultValue,
	intl,
	onValidate = () => {},
	isColorInverted,
	isArchiveInProgress,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [hasNote, setHasNote] = useState<boolean>(false);

	useEffect(() => {
		noteRef.current?.focus();

		// Move cursor to the end
		const noteLength = noteRef.current?.value?.length || 0;
		noteRef.current?.setSelectionRange(noteLength, noteLength);
	}, [noteRef]);

	useEffect(() => {
		fireScreenEvent({
			createAnalyticsEvent,
			name: analyticsSource,
		});
	}, [createAnalyticsEvent, analyticsSource]);

	const characterLimit = ARCHIVE_PAGES_WITH_NOTE_CHARACTER_LIMIT;

	const validate = (value: string | undefined) => {
		setHasNote(!!value);
		const isValid = (value && value.length <= characterLimit) || !value;
		onValidate(isValid);
		if (!isValid) {
			return 'CHAR_LIMIT_EXCEEDED';
		}

		return undefined;
	};

	return (
		<div>
			{fg('platform-design_system_team-form_conversion') ? (
				<Form onSubmit={() => {}}>
					<Field
						label={
							<InverseLabelColor isColorInverted={isColorInverted}>
								<FormattedMessage {...i18n.noteTitle} />
							</InverseLabelColor>
						}
						name="note"
						validate={validate}
						defaultValue={defaultValue}
					>
						{({ fieldProps, error }: any) => (
							<>
								<TextArea
									ref={noteRef}
									placeholder={intl.formatMessage(i18n.notePlaceholder)}
									minimumRows={1}
									{...fieldProps}
									name="ArchivedPageNoteInput-TextArea"
									isDisabled={isArchiveInProgress}
								/>
								<HelperMessage>
									<InverseLabelColor isColorInverted={isColorInverted}>
										{fieldProps.value ? fieldProps.value.length : 0}/{characterLimit}
									</InverseLabelColor>
								</HelperMessage>
								{error === 'CHAR_LIMIT_EXCEEDED' && (
									<HelperMessage>
										<ErrorWrapper isColorInverted={isColorInverted}>
											<StatusErrorIcon label="error" />
											<FormattedMessage {...i18n.characterLimitError} />
										</ErrorWrapper>
									</HelperMessage>
								)}
								{includeNestedCheckbox && hasNote && (
									<Box xcss={nestedCheckboxWrapperStyles}>
										<Checkbox
											label={intl.formatMessage(i18n.applyNoteToNestedPagesLabel)}
											onChange={onNestedCheckboxChange}
											isChecked={isNestedCheckboxChecked}
											testId="cb-apply-note-nested-pages"
										/>
									</Box>
								)}
							</>
						)}
					</Field>
				</Form>
			) : (
				<Form onSubmit={() => {}}>
					{({ formProps }) => (
						<form {...formProps}>
							<Field
								label={
									<InverseLabelColor isColorInverted={isColorInverted}>
										<FormattedMessage {...i18n.noteTitle} />
									</InverseLabelColor>
								}
								name="note"
								validate={validate}
								defaultValue={defaultValue}
							>
								{({ fieldProps, error }: any) => (
									<>
										<TextArea
											ref={noteRef}
											placeholder={intl.formatMessage(i18n.notePlaceholder)}
											minimumRows={1}
											{...fieldProps}
											name="ArchivedPageNoteInput-TextArea"
											isDisabled={isArchiveInProgress}
										/>
										<HelperMessage>
											<InverseLabelColor isColorInverted={isColorInverted}>
												{fieldProps.value ? fieldProps.value.length : 0}/{characterLimit}
											</InverseLabelColor>
										</HelperMessage>
										{error === 'CHAR_LIMIT_EXCEEDED' && (
											<HelperMessage>
												<ErrorWrapper isColorInverted={isColorInverted}>
													<StatusErrorIcon label="error" />
													<FormattedMessage {...i18n.characterLimitError} />
												</ErrorWrapper>
											</HelperMessage>
										)}
										{includeNestedCheckbox && hasNote && (
											<Box xcss={nestedCheckboxWrapperStyles}>
												<Checkbox
													label={intl.formatMessage(i18n.applyNoteToNestedPagesLabel)}
													onChange={onNestedCheckboxChange}
													isChecked={isNestedCheckboxChecked}
													testId="cb-apply-note-nested-pages"
												/>
											</Box>
										)}
									</>
								)}
							</Field>
						</form>
					)}
				</Form>
			)}
		</div>
	);
};

export const ArchivedPageNoteInput = injectIntl(ArchivedPageNoteInputComponent);
